import { ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import type { FunctionComponent, PropsWithChildren } from 'react';

import { Button } from '~/ui/core/button/Button';
import { IconExternalLink } from '~/ui/core/icons';
import { theme } from '~/ui/theme/cobrainer-theme';

import errorPageIllustration from './error-page-illustration.jpg';

type ErrorPageProps = PropsWithChildren<{
    readonly errorCode?: number;
    readonly message: string;
    readonly title: string;
}>;

const Container = styled.div(({ theme }) => ({
    alignItems: 'center',
    backgroundColor: 'var(--semantic-surface-raised)',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100dvh',
    justifyContent: 'center',
    maxHeight: '100%',
    minHeight: 0,
    overflow: 'hidden',
    padding: `${theme.spacing['10xl']} ${theme.spacing['13xl']}`,
}));

const ContentWrapper = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    gap: theme.spacing['10xl'],
    maxWidth: '26rem',
}));

const LogoSection = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
}));

const Logo = styled.div({
    height: '1rem',
    width: '2.2rem',
});

// biome-ignore lint/correctness/noEmptyPattern: suppressed due to migration
const Content = styled.div(({}) => ({
    display: 'flex',
    flexDirection: 'column',
}));

// biome-ignore lint/correctness/noEmptyPattern: suppressed due to migration
const Headline = styled.div(({}) => ({
    display: 'flex',
    flexDirection: 'column',
}));

const Title = styled.h1(({ theme }) => ({
    ...theme.typography.headline.h4.regular,
    color: 'var(--semantic-text-primary)',
    margin: 0,
}));

const Message = styled.p(({ theme }) => ({
    ...theme.typography.body.S.regular,
    color: 'var(--semantic-text-secondary)',
    margin: 0,
}));

const ButtonsContainer = styled.div(({ theme }) => ({
    alignItems: 'flex-start',
    display: 'flex',
    gap: theme.spacing.m,
}));

const IllustrationContainer = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    flex: '1 1 auto',
    justifyContent: 'center',
    marginTop: theme.spacing['10xl'],
    minHeight: 0,
    width: '100%',
}));

const Illustration = styled.img(({ theme }) => ({
    borderRadius: theme.borderRadius.xxxl,
    boxShadow: theme.shadows.large,
    display: 'block',
    height: 'auto',
    maxHeight: '100%',
    maxWidth: 'min(100%, 26rem)',
    overflow: 'hidden',
    width: 'auto',
}));

const mailtoLink = 'mailto:feedback@cobrainer.com';
const statusPageUrl = 'https://status.cobrainer.ai';
const subject = 'Error Cobrainer App';

const mailto = (): void => {
    window.open(`${mailtoLink}?subject=${subject}`);
};

export const ErrorPage: FunctionComponent<ErrorPageProps> = (props) => (
    <ThemeProvider theme={theme}>
        <Container>
            <ContentWrapper>
                <Content>
                    <LogoSection>
                        <Logo />
                    </LogoSection>
                    <Headline>
                        <Title>{props.title}</Title>
                        <Message>{props.message}</Message>
                    </Headline>
                </Content>
                <ButtonsContainer>
                    {props.children ?? (
                        <>
                            <Button variant="tertiary" onClick={mailto}>
                                Provide feedback
                            </Button>
                            <Button
                                as="a"
                                href={statusPageUrl}
                                iconPosition="trailing"
                                rel="noopener noreferrer"
                                target="_blank"
                                variant="tertiary"
                            >
                                Status page
                                <IconExternalLink aria-hidden={true} size={14} />
                            </Button>
                            <Button variant="primary" onClick={() => (window.location.href = '/')}>
                                Reload
                            </Button>
                        </>
                    )}
                </ButtonsContainer>
            </ContentWrapper>
            <IllustrationContainer>
                <Illustration alt="" aria-hidden="true" height={2000} src={errorPageIllustration} width={843} />
            </IllustrationContainer>
        </Container>
    </ThemeProvider>
);
